import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { Building2, Globe, ShieldCheck, Undo2, BadgeCheck } from "lucide-react";
import { getBusinessProfile } from "@/lib/business.functions";
import { formatOrgNumber } from "@/lib/business";
import { adImageSrc } from "@/lib/image-src";

const businessQuery = (slug: string) =>
  queryOptions({
    queryKey: ["business", slug],
    queryFn: () => getBusinessProfile({ data: { slug } }),
    staleTime: 60_000,
  });

export const Route = createFileRoute("/foretag/$slug")({
  loader: ({ context, params }) => context.queryClient.ensureQueryData(businessQuery(params.slug)),
  head: ({ loaderData, params }) => {
    const name = loaderData?.company.name ?? "Företag";
    const title = `${name} – annonser och köpvillkor | Sellavo`;
    const description = loaderData
      ? `Se alla annonser från ${name} på Sellavo. Företagsförsäljning med ångerrätt, reklamationsrätt och tydliga returvillkor.`
      : "Företagsprofil på Sellavo.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary" },
        ...(loaderData ? [] : [{ name: "robots", content: "noindex" }]),
      ],
      links: [{ rel: "canonical", href: `https://sellavo.com/foretag/${params.slug}` }],
    };
  },
  errorComponent: () => (
    <main className="mx-auto w-full max-w-4xl px-4 py-16 text-center sm:px-6 lg:px-8">
      <h1 className="font-heading text-2xl font-bold text-foreground">Företaget finns inte</h1>
      <p className="mt-2 text-muted-foreground">
        Profilen kan ha tagits bort eller bytt adress.{" "}
        <Link to="/annonser" className="font-semibold text-primary hover:underline">
          Se alla annonser
        </Link>
      </p>
    </main>
  ),
  notFoundComponent: () => (
    <main className="mx-auto w-full max-w-4xl px-4 py-16 text-center sm:px-6 lg:px-8">
      <h1 className="font-heading text-2xl font-bold text-foreground">Företaget finns inte</h1>
    </main>
  ),
  component: BusinessPage,
});

function BusinessPage() {
  const { slug } = Route.useParams();
  const { data } = useSuspenseQuery(businessQuery(slug));
  const c = data.company;

  return (
    <main className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      <header className="rounded-xl border border-border bg-card p-5 shadow-sm sm:p-6">
        <div className="flex items-start gap-4">
          {c.logoUrl ? (
            <img
              src={adImageSrc(c.logoUrl, 200)}
              alt={`${c.name} logotyp`}
              className="h-14 w-14 rounded-lg object-contain"
            />
          ) : (
            <span className="flex h-14 w-14 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Building2 className="h-7 w-7" />
            </span>
          )}
          <div className="min-w-0">
            {c.verificationStatus === "verified" ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-bold text-primary">
                <BadgeCheck className="h-3.5 w-3.5" /> Företag
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-0.5 text-xs font-bold text-muted-foreground">
                <BadgeCheck className="h-3.5 w-3.5" /> Ej verifierat företag
              </span>
            )}

            <h1 className="mt-1 font-heading text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl">
              {c.name}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Org.nr {formatOrgNumber(c.orgNumber)}
              {c.vatNumber ? ` · Momsnr ${c.vatNumber}` : ""}
            </p>
            {c.website && (
              <a
                href={c.website}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
              >
                <Globe className="h-4 w-4" /> {c.website.replace(/^https?:\/\//, "")}
              </a>
            )}
          </div>
        </div>
        {c.about && <p className="mt-4 text-sm leading-relaxed text-foreground/85">{c.about}</p>}
      </header>

      <section className="mt-6 grid gap-4 md:grid-cols-3">
        <Terms icon={<Undo2 className="h-4 w-4" />} title="Ångerrätt" body={c.rightOfWithdrawal} />
        <Terms
          icon={<ShieldCheck className="h-4 w-4" />}
          title="Reklamation och garanti"
          body={c.warrantyPolicy}
        />
        <Terms icon={<Undo2 className="h-4 w-4" />} title="Returer" body={c.returnPolicy} />
      </section>

      {c.openingHours && (
        <section className="mt-6 rounded-xl border border-border bg-card p-5">
          <h2 className="font-semibold">Öppettider</h2>
          <dl className="mt-2 grid max-w-sm grid-cols-2 gap-y-1 text-sm">
            {(["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const).map((d, i) => {
              const h = c.openingHours?.[d];
              return h ? (
                <div key={d} className="contents">
                  <dt className="text-muted-foreground">{["Måndag", "Tisdag", "Onsdag", "Torsdag", "Fredag", "Lördag", "Söndag"][i]}</dt>
                  <dd className="tabular-nums">{h.closed ? "Stängt" : `${h.open}–${h.close}`}</dd>
                </div>
              ) : null;
            })}
          </dl>
        </section>
      )}

      <h2 className="mt-8 font-heading text-xl font-bold text-foreground">
        Annonser från {c.name} ({data.ads.length})
      </h2>
      {data.ads.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">Inga aktiva annonser just nu.</p>
      ) : (
        <ul className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.ads.map((ad) => (
            <li key={ad.slug}>
              <Link
                to="/a/$slug"
                params={{ slug: ad.slug }}
                className="block overflow-hidden rounded-xl border border-border bg-card shadow-sm transition hover:border-primary/50"
              >
                {ad.image_url && (
                  <img
                    src={adImageSrc(ad.image_url, 400)}
                    alt={ad.title}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover"
                  />
                )}
                <div className="p-4">
                  <p className="font-semibold text-foreground line-clamp-2">{ad.title}</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {ad.location} · {ad.price?.toLocaleString("sv-SE")} kr
                  </p>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}

function Terms({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string | null;
}) {
  if (!body) return null;
  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
      <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <span className="text-primary">{icon}</span>
        {title}
      </p>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
    </div>
  );
}
